import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Box, Icon, useColorModeValue } from "@chakra-ui/react";

import { FnHandleFileDrop } from "./types";
import { CollecticonFolder, CollecticonPicture } from "@devseed-ui/collecticons-chakra";

type Props = {
  children: ReactNode
  handleFileDrop: FnHandleFileDrop
}

function DropZone({ children, handleFileDrop }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const bgColor = useColorModeValue("primary.50", "primary.700");
  const borderColor = useColorModeValue("base.500", "base.50");
  const dragBorderColor = useColorModeValue("primary.700", "primary.100");

  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [ dragOver, setDragOver ] = useState<boolean>(false);
  const [ fileName, setFileName ] = useState<string>();

  const handleFileSelect = useCallback((files?: FileList) => {
    handleFileDrop(files ? files[0] : undefined);
    setFileName(files ? files[0].name : undefined);
  }, [handleFileDrop]);

  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      setDragOver(true);
    };
    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
    };
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFileSelect(e.dataTransfer?.files);
    };

    dropZone.addEventListener("dragover", handleDragOver);
    dropZone.addEventListener("dragleave", handleDragLeave);
    dropZone.addEventListener("drop", handleDrop);

    return () => {
      dropZone.removeEventListener("dragover", handleDragOver);
      dropZone.removeEventListener("dragleave", handleDragLeave);
      dropZone.removeEventListener("drop", handleDrop);
    };
  }, [handleFileSelect]);

  return (
    <Box
      border="2px dashed"
      borderRadius="4"
      p="2"
      display="flex"
      gap="2"
      alignItems="center"
      ref={dropZoneRef}
      bg={dragOver ? bgColor : "inherit"}
      borderColor={dragOver ? dragBorderColor : borderColor}
      onClick={() => inputRef.current?.click()}
      _hover={{
        cursor: "pointer"
      }}
    >
      <Icon as={fileName ? CollecticonPicture : CollecticonFolder} boxSize="8" />
      <Box>
        { fileName ? <b>{fileName}</b> : children }
      </Box>
      <input
        ref={inputRef}
        type="file"
        name="file"
        onChange={(e) => handleFileSelect(e.target.files || undefined)}
        aria-describedby="panchromatic-file-label"
        style={{ display: "none" }}
      />
    </Box>
  );
}

export default DropZone;
